<script lang="ts" setup>
import ProgressInfoSection from "@/views/trips/monitor/components/ProgressInfoSection.vue";
import RouteAlertMessage from "@/views/trips/monitor/components/RouteAlertMessage.vue";
import ExpandButton from "@/components/controls/ExpandButton.vue";
import TripTruckInfo from "@/views/trips/monitor/components/TripTruckInfo.vue";
import BrokerUpdateSection from "@/views/trips/monitor/components/BrokerUpdateSection.vue";
import LastCheckCallSection from "@/views/trips/monitor/components/LastCheckCallSection.vue";
import TripStopEstimates from "@/views/trips/monitor/components/TripStopEstimates.vue";
import NextRouteStopSection from "@/views/trips/monitor/components/NextRouteStopSection.vue";
import OrderInfo from "@/views/trips/monitor/components/OrderInfo.vue";
import TripTruckLocation from "@/views/trips/monitor/components/TripTruckLocation.vue";
import type {
  TripMonitorItem,
  TripMonitorRoute,
  TripMonitorRouteMap,
  TripMonitorTravelOrder,
  TripMonitorTruck,
} from "@/api/trips/dto/trip-monitor";
import { computed, ref, type VNodeRef } from "vue";
import { RouteStatus } from "@/data/trip";
import { RouteRedispatchState } from "@/enums/trip";
import type { TripUpdate } from "@/api/trips/dto/update";
import type { TripRouteDto } from "@/api/trips/dto/trip.dto";
import dayjs from "dayjs";
import { TripMonitorHiddenRouteStatuses } from "@/data/route";
import TripStops from "@/views/trips/monitor/components/TripStops.vue";
import PermissionTooltip from "@/components/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const hiddenRouteStatues = [RouteStatus.cancelled, RouteStatus.completed];

const showAddCheckCallDialog = defineModel<boolean>("showAddCheckCallDialog", { default: false });
const showConfirmDispatchDialog = defineModel<boolean>("showConfirmDispatchDialog", { default: false });
const showCheckCallIntervalDialog = defineModel<boolean>("showCheckCallIntervalDialog", { default: false });

const showCreateBrokerUpdateDialog = defineModel<boolean>("showCreateBrokerUpdateDialog", { default: false });
const showBrokerUpdateInfoDialog = defineModel<boolean>("showBrokerUpdateInfoDialog", { default: false });
const showBrokerUpdateIntervalDialog = defineModel<boolean>("showBrokerUpdateIntervalDialog", { default: false });

const showActionDialog = defineModel<boolean>("showActionDialog", { default: false });
const showRouteInfoDialog = defineModel<boolean>("showRouteInfoDialog", { default: false });
const actionDialogLoading = defineModel<boolean>("actionDialogLoading", { default: false });

const showRedispatchDialog = defineModel<boolean>("showRedispatchDialog", { default: false });

const props = defineProps<{
  trip: TripMonitorItem;
  selectedTripMonitorRoute: TripMonitorRoute | null;
  updatedRouteIds: number[];
  routesByTruckId: TripMonitorRouteMap;
}>();

const emits = defineEmits<{
  (e: "setRouteDialogData", tripId: number, route: TripMonitorRoute): void;
  (
    e: "setBrokerUpdateDialogData",
    route: TripMonitorRoute,
    travelOrder: TripMonitorTravelOrder | null,
    tripId: number,
    update?: TripUpdate | null,
  ): void;
  (e: "setLastCheckCallDialogsData", route: TripMonitorRoute, tripId: number): void;
  (e: "setRedispatchDialogData", route: TripRouteDto, truck: TripMonitorTruck, tripId: number): void;
}>();

const showAllRoutes = ref<boolean>(false);
const routeRef = ref<VNodeRef | null>(null);

const printRoutes = computed(() => {
  if (showAllRoutes.value) {
    return props.trip.routes;
  }

  const filtered = props.trip.routes.filter((route) => !TripMonitorHiddenRouteStatuses.includes(route.status));

  if (filtered.length === 0) {
    return [props.trip.routes[0]];
  }

  return filtered;
});

const handleRoutesButtonTitle = computed(() => {
  if (showAllRoutes.value) {
    return "Hide routes";
  }

  return "Show routes";
});

const cancelledOrCompletedRoutes = computed(() => {
  return props.trip.routes.filter((route) => {
    return hiddenRouteStatues.includes(route.status);
  });
});

function setRouteDialogData(tripId: number, route: TripMonitorRoute) {
  emits("setRouteDialogData", tripId, route);
}

function setBrokerUpdateDialogData(
  route: TripMonitorRoute,
  travelOrder: TripMonitorTravelOrder | null,
  tripId: number,
  update?: TripUpdate | null,
) {
  emits("setBrokerUpdateDialogData", route, travelOrder, tripId, update);
}

function setLastCheckCallDialogsData(route: TripMonitorRoute, tripId: number) {
  emits("setLastCheckCallDialogsData", route, tripId);
}

function setRedispatchDialogData(route: TripRouteDto, truck: TripMonitorTruck, tripId: number) {
  emits("setRedispatchDialogData", route, truck, tripId);
}

const openedPanels = ref<number[]>([]);

function checkIsCheckCallInFuture(route: TripMonitorRoute) {
  return dayjs(route.lastCheckCall?.checkCallTime).isAfter(dayjs().add(1, "minute"));
}

function getRouteClasses(route: TripMonitorRoute): string {
  const classes: string[] = [];

  if (checkIsCheckCallInFuture(route) || route.status === RouteStatus.completed) {
    classes.push("bg-blue-grey-lighten-5 border-blue-grey-lighten-1");
  } else if (route.status === RouteStatus.cancelled) {
    classes.push("bg-red-lighten-5");
  } else {
    classes.push("bg-grey-lighten-4");
  }

  if (!route.dispatchConfirmed || route.redispatch === RouteRedispatchState.notConfirmed) {
    classes.push("border-warning");
  } else {
    classes.push("border-grey-lighter");
  }

  if (route.status === RouteStatus.planned) {
    classes.push("border-indigo-lighten-2");
  }

  if (props.updatedRouteIds.includes(route.id)) {
    classes.push("trip__route--highlighted");
  }

  return classes.join(" ");
}

function togglePanel(id: number) {
  if (openedPanels.value.includes(id)) {
    openedPanels.value = openedPanels.value.filter((openedId) => openedId !== id);
  } else {
    openedPanels.value.push(id);
  }
}
</script>

<template>
  <div class="trip bg-white px-3 py-2 rounded-s">
    <div class="trip__info d-flex align-center">
      <v-row class="w-100">
        <v-col cols="6">
          <order-info v-for="order in trip.orders" :key="order.id" :order="order" />
        </v-col>

        <v-col cols="6">
          <trip-stops :trip-stops="trip.tripStops" />
        </v-col>
      </v-row>

      <div class="d-flex align-center"></div>

      <v-spacer></v-spacer>

      <v-btn
        v-if="cancelledOrCompletedRoutes.length && trip.routes.length > 1"
        class="mr-4"
        color="primary"
        density="comfortable"
        size="small"
        variant="text"
        @click="showAllRoutes = !showAllRoutes"
        >{{ handleRoutesButtonTitle }} {{ printRoutes.length }}
      </v-btn>

      <permission-tooltip :permission="DispatcherPermissionValue.ViewTripInfo">
        <template #activator="{ disabled }">
          <v-btn
            :disabled="disabled"
            :to="{ name: 'trip-view', params: { id: trip.id } }"
            class="font-size-11 font-weight-600 text-uppercase"
            color="primary"
            density="comfortable"
            size="small"
            target="_blank"
            variant="outlined"
          >
            View trip
          </v-btn>
        </template>

        {{ t("permission.view-trip") }}
      </permission-tooltip>
    </div>

    <v-expansion-panels v-model="openedPanels">
      <v-expansion-panel
        v-for="route in printRoutes"
        :key="route.id"
        :ref="routeRef"
        :class="getRouteClasses(route)"
        :data-route-id="route.id"
        :value="route.id"
        class="trip__route rounded-s mt-3"
        hide-actions
        readonly
      >
        <v-expansion-panel-title class="trip__expansion-title">
          <div class="px-4">
            <trip-truck-info
              :route="route"
              :truck="route.truck"
              :truck-routes="props.routesByTruckId[route.truck.id]"
              class="mb-2"
            ></trip-truck-info>
            <trip-truck-location :truck="route.truck"></trip-truck-location>
          </div>
          <div class="px-4">
            <last-check-call-section
              v-model:show-add-check-call-dialog="showAddCheckCallDialog"
              v-model:show-confirm-dispatch-dialog="showConfirmDispatchDialog"
              v-model:show-interval-dialog="showCheckCallIntervalDialog"
              :route="route"
              :trip="trip"
              @set-dialogs-data="setLastCheckCallDialogsData"
            ></last-check-call-section>
          </div>
          <div class="px-4">
            <broker-update-section
              v-model:show-create-dialog="showCreateBrokerUpdateDialog"
              v-model:show-info-dialog="showBrokerUpdateInfoDialog"
              v-model:show-interval-dialog="showBrokerUpdateIntervalDialog"
              :orders="trip.orders"
              :route="route"
              :trip-id="trip.id"
              @set-dialogs-data="setBrokerUpdateDialogData"
            />
          </div>
          <div class="px-4">
            <progress-info-section :route="route" />
          </div>
          <div class="px-4">
            <next-route-stop-section
              v-if="route.nextRouteStop"
              v-model:show-action-dialog="showActionDialog"
              v-model:show-info-dialog="showRouteInfoDialog"
              :loading="actionDialogLoading"
              :next-route-stop="route.nextRouteStop"
              :route-monitor="route"
              :selected-route="selectedTripMonitorRoute"
              :trip-id="trip.id"
              @set-dialog-data="setRouteDialogData"
            ></next-route-stop-section>
          </div>
          <div class="px-4 d-flex">
            <trip-stop-estimates :route="route" class="mr-2"></trip-stop-estimates>

            <div class="d-flex align-start ml-auto">
              <v-btn class="mr-3" density="compact" icon="mdi-dots-horizontal" size="xsmall" variant="plain"></v-btn>
              <expand-button :expanded="openedPanels.includes(route.id)" size="xsmall" @click="togglePanel(route.id)" />
            </div>
          </div>

          <route-alert-message
            v-model="showRedispatchDialog"
            :trip-id="trip.id"
            :trip-monitor-route="route"
            class="mx-4 px-2 mt-3 trip__route-alert"
            @set-dialog-data="setRedispatchDialogData"
          />
        </v-expansion-panel-title>
        <!--                <v-expansion-panel-text> </v-expansion-panel-text>-->
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.trip {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
  margin-bottom: 8px;
  border: 1px solid $grey-light;

  &__info {
    grid-column: 1 / -1;
  }

  &__route {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    border-radius: 4px;
    padding: 12px 0;
  }

  &__route--highlighted {
    border-width: 2px;
    animation: highlightFlash 3s ease-out;
  }

  @keyframes highlightFlash {
    0% {
      background: $blue-grey-lighten-5;
      border-color: $indigo-accent-1;
    }

    80% {
      background: $blue-grey-lighten-5;
      border-color: $indigo-accent-1;
    }

    100% {
      background: transparent;
      border-color: transparent;
    }
  }

  &__expansion-title {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    padding: 0;
    align-items: flex-start;
    user-select: text;
  }

  .v-expansion-panels {
    :deep {
      .v-expansion-panel__shadow {
        display: none;
      }

      .v-expansion-panel-title__overlay {
        display: none;
      }
    }
  }

  &__route-alert {
    grid-column: 1 / -1;
    height: auto;
  }
}
</style>
